import React, { useEffect, useRef } from "react";

const AboutSection: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const parallaxImageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const image = parallaxImageRef.current;

    const scrollContainer = document.getElementById("app-scroll-container");

    if (!section || !image || !scrollContainer) return;

    let animationFrame: number | null = null;

    const updateParallax = () => {
      const sectionRect = section.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();
      const viewportHeight = scrollContainer.clientHeight;

      const sectionTopRelative = sectionRect.top - containerRect.top;
      const totalDistance = viewportHeight + sectionRect.height;

      let progress = (viewportHeight - sectionTopRelative) / totalDistance;

      progress = Math.max(0, Math.min(1, progress));

      const maxMovement = window.innerWidth < 768 ? 40 : 120;

      const translateY = maxMovement - progress * maxMovement * 2;

      image.style.transform = `translate3d(0, ${translateY}px, 0) scale(1.15)`;

      animationFrame = null;
    };

    const handleScroll = () => {
      if (animationFrame !== null) return;

      animationFrame = requestAnimationFrame(updateParallax);
    };

    updateParallax();

    scrollContainer.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    window.addEventListener("resize", handleScroll);

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);

      if (animationFrame !== null) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      id="about"
      className="lg:min-h-screen flex flex-col lg:justify-center bg-black text-white py-10 md:py-14 lg:py-16 px-5 md:px-6 lg:px-20 relative overflow-hidden"
    >
      {/* Fondo decorativo */}
      <div className="absolute top-0 right-0 bottom-0 w-[92%] md:w-[80%] z-0 blur-md md:blur-lg opacity-45 md:opacity-70 scale-x-[-1] overflow-hidden">
        <img
          src="https://res.cloudinary.com/xvzxvfee/image/upload/v1788743200/fondo_kk4tqb.webp"
          alt=""
          loading="lazy"
          decoding="async"
          aria-hidden="true"
          className="h-full w-full object-cover object-right"
        />
      </div>

      {/* Título superior */}
      <div className="text-center relative z-40 px-4 mt-10">
        <h2 className="text-xl md:text-2xl font-bold tracking-[4px]">
          SOBRE NOSOTROS
        </h2>
      </div>

      {/* Card principal */}
      <div className="w-full max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8 lg:gap-12 mt-6 md:mt-10 lg:mt-12 rounded-[28px] md:rounded-[50px] overflow-hidden z-20 relative">
        {/* Imagen con parallax */}
        <img
          ref={parallaxImageRef}
          src="https://res.cloudinary.com/xvzxvfee/image/upload/v1788743201/fondo2_esvctd.webp"
          alt=""
          loading="lazy"
          decoding="async"
          aria-hidden="true"
          className="absolute -top-[1%] left-0 z-0 h-[120%] w-full object-cover object-center scale-[1.15] will-change-transform pointer-events-none"
          style={{
            transform: "translate3d(0, 60px, 0) scale(1.15)",
          }}
        />

        {/* Oscurecimiento ligero */}
        <div className="absolute inset-0 z-[1] bg-black/5 pointer-events-none" />

        {/* Columna izquierda */}
        <div className="lg:col-span-2 flex justify-center items-center relative z-10">
          <div className="w-full h-24 sm:h-40 md:h-56 lg:h-96 overflow-hidden" />
        </div>

        {/* Columna derecha */}
        <div className="lg:col-span-3 relative z-10">
          {/* Fondo con blur */}
          <div className="absolute inset-0 bg-black/35 md:bg-black/30 backdrop-blur-md z-0" />

          {/* Contenido */}
          <div className="relative z-10 p-5 sm:p-7 md:p-8 lg:p-12 text-center lg:text-right">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 md:mb-6 tracking-[4px] md:tracking-[12px]">
              <span className="bg-gradient-to-r from-prin1 to-prin2 bg-clip-text text-transparent">
                TECNO
              </span>

              <span className="text-transparent stroke-yellow">
                LOGÍA QUE <br />
                CRECE{" "}
              </span>

              <span className="bg-gradient-to-r from-prin2 to-prin3 bg-clip-text text-transparent">
                CONTIGO
              </span>
            </h3>

            <h4 className="text-base sm:text-lg md:text-2xl font-semibold text-white mb-3 md:mb-4 leading-snug">
              SOFTWARE HECHO A TU MEDIDA
            </h4>

            <p className="text-gray-300 text-sm sm:text-base leading-relaxed max-w-none lg:max-w-xl lg:ml-auto">
              En SprintCore combinamos creatividad, diseño y tecnología para
              convertir necesidades reales en soluciones digitales funcionales.
              <br />
              <br />
              Nuestro equipo de ingenieros de software reúne más de 5 años de
              experiencia desarrollando productos digitales, con un enfoque
              cercano y soluciones pensadas para evolucionar junto a cada
              proyecto.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
